import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import {
  Code,
  Copy,
  Check,
  Search,
  Table as TableIcon,
  ChevronDown,
  ChevronRight,
  Download,
  Maximize2,
  Minimize2,
  CheckCircle2,
  AlertCircle,
  RefreshCw,
  HelpCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '../components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import JSONQuiz from './JSONQuiz';

const SYNTAX_COLORS = {
  key: 'text-navigate-gold',
  string: 'text-green-400',
  number: 'text-blue-400',
  boolean: 'text-[#1EBFB3]',
  null: 'text-zinc-500',
  bracket: 'text-zinc-400',
};

export default function JSONContentPlayer({ 
  content, 
  fileUrl, 
  token, 
  onComplete,
  isCompleted = false 
}) {
  const [jsonData, setJsonData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('tree');
  const [searchTerm, setSearchTerm] = useState('');
  const [copied, setCopied] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [expandedPaths, setExpandedPaths] = useState(new Set(['root']));

  useEffect(() => {
    fetchJSONContent();
  }, [fileUrl]);

  const fetchJSONContent = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.get(fileUrl, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setJsonData(response.data);
      
      // Auto-expand first level
      const initialExpanded = new Set(['root']);
      if (typeof response.data === 'object' && response.data !== null) {
        Object.keys(response.data).slice(0, 5).forEach(key => {
          initialExpanded.add(`root.${key}`);
        });
      }
      setExpandedPaths(initialExpanded);
    } catch (err) {
      console.error('Failed to fetch JSON:', err);
      setError('Failed to load JSON content');
    } finally {
      setLoading(false);
    }
  };

  const canShowTable = useMemo(() => {
    if (!jsonData) return false;
    if (!Array.isArray(jsonData)) return false;
    if (jsonData.length === 0) return false;
    return jsonData.every(item => typeof item === 'object' && item !== null && !Array.isArray(item));
  }, [jsonData]);

  const tableColumns = useMemo(() => {
    if (!canShowTable) return [];
    const allKeys = new Set();
    jsonData.forEach(item => {
      Object.keys(item).forEach(key => allKeys.add(key));
    });
    return Array.from(allKeys);
  }, [jsonData, canShowTable]);

  const filteredTableData = useMemo(() => {
    if (!canShowTable || !searchTerm) return jsonData;
    const lowerSearch = searchTerm.toLowerCase();
    return jsonData.filter(row => 
      Object.values(row).some(val => 
        String(val).toLowerCase().includes(lowerSearch)
      )
    );
  }, [jsonData, canShowTable, searchTerm]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(jsonData, null, 2));
      setCopied(true);
      toast.success('JSON copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Failed to copy');
    }
  };

  const handleDownload = () => {
    const blob = new Blob([JSON.stringify(jsonData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = content?.title || 'data.json';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('JSON downloaded!');
  };

  const togglePath = (path) => {
    setExpandedPaths(prev => {
      const newSet = new Set(prev);
      if (newSet.has(path)) {
        newSet.delete(path);
      } else {
        newSet.add(path);
      }
      return newSet;
    });
  };

  const expandAll = () => {
    const allPaths = new Set(['root']);
    const collectPaths = (obj, path = 'root') => {
      if (typeof obj === 'object' && obj !== null) {
        Object.keys(obj).forEach(key => {
          const newPath = `${path}.${key}`;
          allPaths.add(newPath);
          collectPaths(obj[key], newPath);
        });
      }
    };
    collectPaths(jsonData);
    setExpandedPaths(allPaths);
  };

  const collapseAll = () => {
    setExpandedPaths(new Set(['root']));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full bg-zinc-900">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-navigate-gold border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-zinc-400">Loading JSON content...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full bg-zinc-900">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
          <p className="text-red-400 mb-4">{error}</p>
          <button
            onClick={fetchJSONContent}
            className="flex items-center gap-2 px-4 py-2 bg-zinc-800 text-white rounded-lg hover:bg-zinc-700"
          >
            <RefreshCw className="h-4 w-4" />
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`flex flex-col h-full bg-zinc-900 ${isFullscreen ? 'fixed inset-0 z-50' : ''}`}>
      {/* Header */}
      <div className="px-6 py-4 border-b border-zinc-800 flex items-center justify-between shrink-0 bg-navigate-surface">
        <div className="flex items-center gap-3">
          <Code className="h-6 w-6 text-navigate-gold" />
          <div>
            <h2 className="font-heading text-lg font-bold text-white">{content?.title || 'JSON Content'}</h2>
            <p className="text-sm text-zinc-400">
              Interactive JSON Learning Object
              {Array.isArray(jsonData) && ` • ${jsonData.length} items`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setIsFullscreen(!isFullscreen)}
            className="p-2 text-zinc-400 hover:text-white rounded-lg hover:bg-zinc-800"
            title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
          >
            {isFullscreen ? <Minimize2 className="h-5 w-5" /> : <Maximize2 className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {/* Toolbar */}
      <div className="px-6 py-3 border-b border-zinc-800 flex items-center justify-between shrink-0 bg-zinc-900/50">
        <div className="flex items-center gap-4">
          {/* View Toggle */}
          <div className="flex items-center bg-zinc-800 rounded-lg p-1">
            <button
              onClick={() => setViewMode('tree')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors flex items-center gap-2 ${
                viewMode === 'tree' 
                  ? 'bg-navigate-gold text-black' 
                  : 'text-zinc-400 hover:text-white'
              }`}
            >
              <Code className="h-4 w-4" />
              Tree View
            </button>
            <button
              onClick={() => setViewMode('table')}
              disabled={!canShowTable}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors flex items-center gap-2 ${
                viewMode === 'table' 
                  ? 'bg-navigate-gold text-black' 
                  : canShowTable 
                    ? 'text-zinc-400 hover:text-white'
                    : 'text-zinc-600 cursor-not-allowed'
              }`}
              title={canShowTable ? 'Table View' : 'Table view only for arrays of objects'}
            >
              <TableIcon className="h-4 w-4" />
              Table
            </button>
            <button
              onClick={() => setViewMode('quiz')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors flex items-center gap-2 ${
                viewMode === 'quiz' 
                  ? 'bg-navigate-gold text-black' 
                  : 'text-zinc-400 hover:text-white'
              }`}
            >
              <HelpCircle className="h-4 w-4" />
              Quiz
            </button>
          </div>

          {/* Search (for table view) */}
          {viewMode === 'table' && canShowTable && (
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
              <Input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search..."
                className="pl-9 w-48 bg-zinc-800 border-zinc-700 h-9"
              />
            </div>
          )}

          {/* Expand/Collapse for tree view */}
          {viewMode === 'tree' && (
            <div className="flex items-center gap-2 text-sm">
              <button onClick={expandAll} className="text-zinc-400 hover:text-white">
                Expand All
              </button>
              <span className="text-zinc-600">|</span>
              <button onClick={collapseAll} className="text-zinc-400 hover:text-white">
                Collapse All
              </button>
            </div>
          )}
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={handleCopy}
            className="p-2 text-zinc-400 hover:text-white bg-zinc-800 rounded-lg"
            title="Copy JSON"
          >
            {copied ? <Check className="h-4 w-4 text-green-400" /> : <Copy className="h-4 w-4" />}
          </button>

          <button
            onClick={handleDownload}
            className="p-2 text-zinc-400 hover:text-white bg-zinc-800 rounded-lg"
            title="Download JSON"
          >
            <Download className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto">
        {viewMode === 'tree' && (
          <div className="p-6">
            <TreeView 
              data={jsonData}
              expandedPaths={expandedPaths}
              onTogglePath={togglePath}
            />
          </div>
        )}
        {viewMode === 'table' && (
          <div className="p-6">
            <TableView
              data={filteredTableData}
              columns={tableColumns}
              searchTerm={searchTerm}
            />
          </div>
        )}
        {viewMode === 'quiz' && (
          <JSONQuiz 
            jsonData={jsonData} 
            onComplete={onComplete}
            token={token}
          />
        )}
      </div>

      {/* Footer with Complete Button - hide in quiz mode */}
      {viewMode !== 'quiz' && (
        <div className="px-6 py-4 border-t border-zinc-800 flex items-center justify-between shrink-0 bg-navigate-surface">
          <div className="text-sm text-zinc-400">
            Explore the JSON data above to learn about its structure
          </div>
          
          {isCompleted ? (
            <div className="flex items-center gap-2 text-green-400">
              <CheckCircle2 className="h-5 w-5" />
              <span className="font-medium">Completed</span>
            </div>
          ) : (
            <button
              onClick={onComplete}
              className="px-4 py-2 bg-navigate-gold text-black rounded-lg font-medium hover:bg-navigate-gold-light transition-colors flex items-center gap-2"
            >
              <CheckCircle2 className="h-5 w-5" />
              Mark Complete
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// Tree View Component
function TreeView({ data, expandedPaths, onTogglePath }) {
  const formattedJSON = useMemo(() => {
    return JSON.stringify(data, null, 2);
  }, [data]);

  const highlightedJSON = useMemo(() => {
    return formattedJSON.replace(
      /("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+\-]?\d+)?)/g,
      (match) => {
        let cls = SYNTAX_COLORS.number;
        if (/^"/.test(match)) {
          if (/:$/.test(match)) {
            cls = SYNTAX_COLORS.key;
          } else {
            cls = SYNTAX_COLORS.string;
          }
        } else if (/true|false/.test(match)) {
          cls = SYNTAX_COLORS.boolean;
        } else if (/null/.test(match)) {
          cls = SYNTAX_COLORS.null;
        }
        return `<span class="${cls}">${match}</span>`;
      }
    );
  }, [formattedJSON]);

  return (
    <div className="font-mono text-sm bg-zinc-800/50 rounded-lg p-4 overflow-x-auto">
      <pre 
        className="whitespace-pre-wrap break-words text-zinc-300 leading-relaxed"
        dangerouslySetInnerHTML={{ __html: highlightedJSON }}
      />
    </div>
  );
}

// Table View Component
function TableView({ data, columns, searchTerm }) {
  if (!data || data.length === 0) {
    return (
      <div className="text-center py-12 text-zinc-400">
        {searchTerm ? 'No matching records found' : 'No data to display'}
      </div>
    );
  }

  return (
    <div className="border border-zinc-800 rounded-lg overflow-hidden">
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-zinc-800/50">
              <TableHead className="text-zinc-400 font-medium w-12">#</TableHead>
              {columns.map((col) => (
                <TableHead key={col} className="text-zinc-300 font-medium whitespace-nowrap">
                  {col}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.map((row, index) => (
              <TableRow key={index} className="border-zinc-800 hover:bg-zinc-800/30">
                <TableCell className="text-zinc-500 font-mono text-xs">{index + 1}</TableCell>
                {columns.map((col) => (
                  <TableCell key={col} className="text-zinc-300 max-w-[300px]">
                    <CellValue value={row[col]} searchTerm={searchTerm} />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <div className="px-4 py-2 bg-zinc-800/30 border-t border-zinc-800 text-xs text-zinc-500">
        Showing {data.length} records
      </div>
    </div>
  );
}

// Cell Value Component
function CellValue({ value, searchTerm }) {
  if (value === null || value === undefined) {
    return <span className="text-zinc-500 italic">null</span>;
  }
  
  if (typeof value === 'boolean') {
    return (
      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
        value ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
      }`}>
        {value.toString()}
      </span>
    );
  }
  
  if (typeof value === 'number') {
    return <span className="font-mono text-blue-400">{value}</span>;
  }
  
  if (Array.isArray(value)) {
    return (
      <span className="text-zinc-400 text-xs bg-zinc-800 px-2 py-1 rounded">
        [{value.length} items]
      </span>
    );
  }
  
  if (typeof value === 'object') {
    return (
      <span className="text-zinc-400 text-xs bg-zinc-800 px-2 py-1 rounded">
        {'{...}'}
      </span>
    );
  }
  
  const strValue = String(value);
  
  if (searchTerm && strValue.toLowerCase().includes(searchTerm.toLowerCase())) {
    const parts = strValue.split(new RegExp(`(${searchTerm})`, 'gi'));
    return (
      <span className="truncate block">
        {parts.map((part, i) => 
          part.toLowerCase() === searchTerm.toLowerCase() ? (
            <mark key={i} className="bg-navigate-gold/30 text-navigate-gold px-0.5 rounded">{part}</mark>
          ) : part
        )}
      </span>
    );
  }
  
  return <span className="truncate block">{strValue}</span>;
}
